import { HTMLAttributes, SVGProps, Suspense, createElement, lazy } from "react";
import styled from "styled-components";
import { ReactComponent as _IconMainWidgetStyle } from "./svg/icon-main-widget-style.svg";
import { ReactComponent as _IconExpansionWidgetStyle } from "./svg/icon-expansion-widget-style.svg";

export type SvgProps = Omit<SVGProps<SVGElement>, "children" | "ref">;

export function lazySvg(
  importFn: () => Promise<{ ReactComponent: React.ComponentType<SvgProps> }>
) {
  const Component = lazy(async () => {
    const mod = await importFn();
    return { default: mod.ReactComponent };
  });

  return (props: SvgProps) => (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  );
}

export const ChartTypeBarIcon = lazySvg(
  () => import("./svg/chart-type-bar-icon.svg")
);
export const ChartTypeBarStackIcon = lazySvg(
  () => import("./svg/chart-type-bar-stack-icon.svg")
);
export const ChartTypeLineIcon = lazySvg(
  () => import("./svg/chart-type-line-icon.svg")
);
export const ChartTypeMultipleLineIcon = lazySvg(
  () => import("./svg/chart-type-multiple-line-icon.svg")
);
export const ChartTypeSingleLineIcon = lazySvg(
  () => import("./svg/chart-type-single-line-icon.svg")
);

export const DeleteReportWidget = lazySvg(
  () => import("./svg/delete-report-widget.svg")
);
export const DragIcon = lazySvg(() => import("./svg/drag-icon.svg"));
export const IconAccident = lazySvg(() => import("./svg/icon-accident.svg"));
export const IconAdmin = lazySvg(() => import("./svg/icon-admin.svg"));
export const IconAfterAction = lazySvg(
  () => import("./svg/icon-after-action.svg")
);
export const IconAlarmLine = lazySvg(() => import("./svg/icon-alarm-line.svg"));
export const IconArea = lazySvg(() => import("./svg/icon-area.svg"));
export const IconArrow = lazySvg(() => import("./svg/icon-arrow.svg"));
export const IconArrowDown = lazySvg(() => import("./svg/icon-arrow-down.svg"));
export const IconArrowDownFill = lazySvg(
  () => import("./svg/icon-arrow-down-fill.svg")
);
export const IconArrowDownLine = lazySvg(
  () => import("./svg/icon-arrow-down-line.svg")
);
export const IconArrowLeft = lazySvg(() => import("./svg/icon-arrow-left.svg"));
export const IconArrowRight = lazySvg(
  () => import("./svg/icon-arrow-right.svg")
);
export const IconArrowUp = lazySvg(() => import("./svg/icon-arrow-up.svg"));
export const IconArrowUpFill = lazySvg(
  () => import("./svg/icon-arrow-up-fill.svg")
);
export const IconBicycle = lazySvg(() => import("./svg/icon-bicycle.svg"));
export const IconBus = lazySvg(() => import("./svg/icon-bus.svg"));
export const IconCalendar = lazySvg(() => import("./svg/icon-calendar.svg"));
export const IconCar = lazySvg(() => import("./svg/icon-car.svg"));
export const IconCarEvent = lazySvg(() => import("./svg/icon-car-event.svg"));
export const IconCaution = lazySvg(() => import("./svg/icon-caution.svg"));
export const IconCheckTick = lazySvg(() => import("./svg/icon-check-tick.svg"));
export const IconClose = lazySvg(() => import("./svg/icon-close.svg"));
export const IconConfirmed = lazySvg(() => import("./svg/icon-confirmed.svg"));
export const IconCrowdSafetyStatistics = lazySvg(
  () => import("./svg/icon-crowd-safety-statistics.svg")
);
export const IconCurrentLocationDot = lazySvg(
  () => import("./svg/icon-current-location-dot.svg")
);
export const IconDanger = lazySvg(() => import("./svg/icon-danger.svg"));
export const IconDashboardSequence = lazySvg(
  () => import("./svg/icon-dashboard-sequence.svg")
);
export const IconDatatypeCreate = lazySvg(
  () => import("./svg/icon-datatype-create.svg")
);
export const IconDetecting = lazySvg(() => import("./svg/icon-detecting.svg"));
export const IconDetectingLight = lazySvg(
  () => import("./svg/icon-detecting-light.svg")
);
export const IconDone = lazySvg(() => import("./svg/icon-done.svg"));
export const IconDown = lazySvg(() => import("./svg/icon-down.svg"));
export const IconEventActive = lazySvg(
  () => import("./svg/icon-event-active.svg")
);

export const IconFilterAllEvent = lazySvg(
  () => import("./svg/icon-filter-all-event.svg")
);
export const IconFix = lazySvg(() => import("./svg/icon-fix.svg"));
export const IconFolderCharacterFill = lazySvg(
  () => import("./svg/icon-folder-character-fill.svg")
);
export const IconForklift = lazySvg(() => import("./svg/icon-forklift.svg"));
export const IconHelmet = lazySvg(() => import("./svg/icon-helmet.svg"));
export const IconImageUpload = lazySvg(
  () => import("./svg/icon-image-upload.svg")
);
export const IconImportDevice = lazySvg(
  () => import("./svg/icon-import-device.svg")
);
export const IconIndeterminate = lazySvg(
  () => import("./svg/icon-indeterminate.svg")
);
export const IconLoader = lazySvg(() => import("./svg/icon-loader.svg"));
export const IconLogoLogin = lazySvg(() => import("./svg/icon-logo-login.svg"));
export const IconMapEvent = lazySvg(() => import("./svg/icon-map-event.svg"));
export const IconMapPinFill = lazySvg(
  () => import("./svg/icon-map-pin-fill.svg")
);
export const IconMesManagement = lazySvg(
  () => import("./svg/icon-mes-management.svg")
);
export const IconMesManagementDone = lazySvg(
  () => import("./svg/icon-mes-management-done.svg")
);
export const IconMesManagementError = lazySvg(
  () => import("./svg/icon-mes-management-error.svg")
);
export const IconMesManagementNoAction = lazySvg(
  () => import("./svg/icon-mes-management-no-action.svg")
);
export const IconMinus = lazySvg(() => import("./svg/icon-minus.svg"));
export const IconMotorcycle = lazySvg(
  () => import("./svg/icon-motorcycle.svg")
);
export const IconNew = lazySvg(() => import("./svg/icon-new.svg"));
export const IconNoAction = lazySvg(() => import("./svg/icon-no-action.svg"));
export const IconNoData = lazySvg(() => import("./svg/icon-no-data.svg"));
export const IconNoDevice = lazySvg(() => import("./svg/icon-no-device.svg"));
export const IconNoPlayer = lazySvg(() => import("./svg/icon-no-player.svg"));
export const IconNoResult = lazySvg(() => import("./svg/icon-no-result.svg"));
export const IconNonCheck = lazySvg(() => import("./svg/icon-non-check.svg"));
export const IconNoneFill = lazySvg(() => import("./svg/icon-none-fill.svg"));
export const IconParking = lazySvg(() => import("./svg/icon-parking.svg"));
export const IconPeople = lazySvg(() => import("./svg/icon-people.svg"));
export const IconPinFill = lazySvg(() => import("./svg/icon-pin-fill.svg"));
export const IconPinPushLine = lazySvg(
  () => import("./svg/icon-pin-push-line.svg")
);
export const IconPlayLine = lazySvg(() => import("./svg/icon-play-line.svg"));
export const IconQuestionMark = lazySvg(
  () => import("./svg/icon-question-mark.svg")
);
export const IconReturnArrow = lazySvg(
  () => import("./svg/icon-return-arrow.svg")
);
export const IconRoot = lazySvg(() => import("./svg/icon-root.svg"));
export const IconRuleForklift = lazySvg(
  () => import("./svg/icon-rule-forklift.svg")
);
export const IconSales = lazySvg(() => import("./svg/icon-sales.svg"));
export const IconShoppingCart = lazySvg(
  () => import("./svg/icon-shopping-cart.svg")
);
export const IconShoppingCartLine = lazySvg(
  () => import("./svg/icon-shopping-cart-line.svg")
);
export const IconSightMind = lazySvg(() => import("./svg/icon-sight-mind.svg"));
export const IconSlipAndFall = lazySvg(
  () => import("./svg/icon-slip-and-fall.svg")
);
export const IconSlipAndFallSingle = lazySvg(
  () => import("./svg/icon-slip-and-fall-single.svg")
);
export const IconSlipFall = lazySvg(() => import("./svg/icon-slip-fall.svg"));
export const IconSort = lazySvg(() => import("./svg/icon-sort.svg"));
export const IconSpeeding = lazySvg(() => import("./svg/icon-speeding.svg"));
export const IconStopLine = lazySvg(() => import("./svg/icon-stop-line.svg"));
export const IconTemperatureEvent = lazySvg(
  () => import("./svg/icon-temperature-event.svg")
);
export const IconThumbnailPlay = lazySvg(
  () => import("./svg/icon-thumbnail-play.svg")
);
export const IconTrafficJamEvent = lazySvg(
  () => import("./svg/icon-traffic-jam-event.svg")
);
export const IconTrafficJamEventMulti = lazySvg(
  () => import("./svg/icon-traffic-jam-event-multi.svg")
);
export const IconTreeViewCheck = lazySvg(
  () => import("./svg/icon-tree-view-check.svg")
);
export const IconTreeViewHalfCheck = lazySvg(
  () => import("./svg/icon-tree-view-half-check.svg")
);
export const IconTreeViewUnCheck = lazySvg(
  () => import("./svg/icon-tree-view-un-check.svg")
);
export const IconTruck = lazySvg(() => import("./svg/icon-truck.svg"));
export const IconUp = lazySvg(() => import("./svg/icon-up.svg"));
export const IconUserProfile = lazySvg(
  () => import("./svg/icon-user-profile.svg")
);
export const IconVest = lazySvg(() => import("./svg/icon-vest.svg"));
export const IconVideoCamera = lazySvg(
  () => import("./svg/icon-video-camera.svg")
);
export const IconVideoEnterFullscreen = lazySvg(
  () => import("./svg/icon-video-enter-fullscreen.svg")
);
export const IconVideoExitFullscreen = lazySvg(
  () => import("./svg/icon-video-exit-fullscreen.svg")
);
export const IconVideoPause = lazySvg(
  () => import("./svg/icon-video-pause.svg")
);
export const IconVideoPlay = lazySvg(() => import("./svg/icon-video-play.svg"));
export const IconVideoSkip = lazySvg(() => import("./svg/icon-video-skip.svg"));
export const IconVideoStop = lazySvg(() => import("./svg/icon-video-stop.svg"));
export const IconWidgetForklift = lazySvg(
  () => import("./svg/icon-widget-forklift.svg")
);
export const IconWorkerAll = lazySvg(() => import("./svg/icon-worker-all.svg"));
export const IconWorkerDay = lazySvg(() => import("./svg/icon-worker-day.svg"));
export const IconZoomIn = lazySvg(() => import("./svg/icon-zoom-in.svg"));
export const IconZoomOut = lazySvg(() => import("./svg/icon-zoom-out.svg"));
export const MixChartLineAndBar = lazySvg(
  () => import("./svg/mix-chart-line-and-bar.svg")
);
export const MixedChartLineAndArea = lazySvg(
  () => import("./svg/mixed-chart-line-and-area.svg")
);
export const SelectWidgetIcon = lazySvg(
  () => import("./svg/select-widget-icon.svg")
);
export const SendEmailVectorIcon = lazySvg(
  () => import("./svg/send-email-vector-icon.svg")
);
export const HandleBar = lazySvg(() => import("./svg/handle-bar.svg"));
export const IconImageNotAvailable = lazySvg(
  () => import("./svg/icon-image-not-available.svg")
);
export const ReportHeatmap = lazySvg(
  () => import("./svg/icon-report-heatmap.svg")
);
export const SightMindLogo = lazySvg(() => import("./svg/sightmind-logo.svg"));
export const HorizontalBar = lazySvg(
  () => import("./svg/icon-horizontal-bar.svg")
);
export const TopCount1 = lazySvg(
  () => import("./svg/report-widget-top-count-1.svg")
);
export const TopCount2 = lazySvg(
  () => import("./svg/report-widget-top-count-2.svg")
);
export const TopCount3 = lazySvg(
  () => import("./svg/report-widget-top-count-3.svg")
);
export const InfoIcon = lazySvg(() => import("./svg/info-icon.svg"));
export const IconFilterOutline = lazySvg(
  () => import("./svg/icon-filter-outline.svg")
);
export const ReportFileIcon = lazySvg(() => import("./svg/report-file.svg"));
export const IconPictogram = lazySvg(
  () => import("./svg/analytics/icon-pictogram.svg")
);
export const IconSend = lazySvg(() => import("./svg/icon-send.svg"));
export const IconUploadExport = lazySvg(
  () => import("./svg/analytics/icon-export.svg")
);
export const IconSmallArrowNone = lazySvg(
  () => import("./svg/small-arrow-none.svg")
);
export const IconUserGuide = lazySvg(() => import("./svg/icon-user-guide.svg"));
export const IconReportAddTextIcon = lazySvg(
  () => import("./svg/report-add-text-icon.svg")
);
export const IconTimeZone = lazySvg(() => import("./svg/icon-time-zone.svg"));
export const IconAppSightMind = lazySvg(
  () => import("./svg/apps/icon-app-sightmind.svg")
);
export const IconAppOnCloud = lazySvg(
  () => import("./svg/apps/icon-app-on-cloud.svg")
);
export const IconAppCloudPortal = lazySvg(
  () => import("./svg/apps/icon-app-cloud-portal.svg")
);
export const IconAppDMPro = lazySvg(
  () => import("./svg/apps/icon-app-dmpro.svg")
);
export const IconAppACaaS = lazySvg(
  () => import("./svg/apps/icon-app-acaas.svg")
);
export const IconAppFlexAI = lazySvg(
  () => import("./svg/apps/icon-app-flex-ai.svg")
);
export const IconAppSightMindDark = lazySvg(
  () => import("./svg/apps/icon-app-sightmind-dark.svg")
);
export const IconAppOnCloudDark = lazySvg(
  () => import("./svg/apps/icon-app-on-cloud-dark.svg")
);
export const IconAppCloudPortalDark = lazySvg(
  () => import("./svg/apps/icon-app-cloud-portal-dark.svg")
);
export const IconAppDMProDark = lazySvg(
  () => import("./svg/apps/icon-app-dmpro-dark.svg")
);
export const IconAppACaaSDark = lazySvg(
  () => import("./svg/apps/icon-app-acaas-dark.svg")
);
export const IconAppFlexAIDark = lazySvg(
  () => import("./svg/apps/icon-app-flex-ai-dark.svg")
);

export const IconRadar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: conic-gradient(#000000, #00b960);
  animation: scan 2s infinite linear;
  @keyframes scan {
    to {
      transform: rotate(1turn);
    }
  }
`;

interface Props extends HTMLAttributes<SVGElement> {
  size?: number;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SvgIcon = (Icon: any, props: Props) => {
  return createElement(Icon, props);
};
export const IconMainWidgetStyle = (props: SvgProps) =>
  SvgIcon(_IconMainWidgetStyle, props);

export const IconExpansionWidgetStyle = (props: SvgProps) =>
  SvgIcon(_IconExpansionWidgetStyle, props);
